import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';

import useUserHook from 'hooks/useUserHook';

import { PageNotFound } from 'components/common/ErrorPages/pageNotFound';
import GuardedRoute from 'components/common/GuardedRoute/guardedRoute';
import NoAccess from 'components/common/NoAccess/noAccess';
import { SideMenuListProps } from 'components/common/SideMenu/SideMenu';
import { MuiIcon } from 'components/common/muiIcon/muiIcon';

import { excellenceAppsConfig } from 'components/Dashboards/ExcellenceApps/excellenceAppsConfig';
import { RedirectToLast } from 'components/Dashboards/Program/ProgramWrapper/redirectToLast';
import { Responsive } from 'components/Dashboards/responsive';
import { ActivitiesGraphPage } from 'components/pages/ActivitiesGraphPage';
import { CompletionPlanPage } from 'components/pages/CompletionPlanPage';
import { DurationForecastPage } from 'components/pages/DurationForecastPage';
import { FinishDatePredictionPage } from 'components/pages/FinishDatePredictionPage';
import LmsPage from 'components/pages/LmsPage';
import { LogicQualityCheckActivitiesPage } from 'components/pages/LogicQualityCheckActivitiesPage';
import { LogicQualityCheckCategoryPage } from 'components/pages/LogicQualityCheckCategoryPage';
import { LogicQualityCheckSummaryPage } from 'components/pages/LogicQualityCheckSummaryPage';
import { LogicQualityCheckValidationPage } from 'components/pages/LogicQualityCheckValidationPage';
import { RecommendationsPage } from 'components/pages/RecommendationsPage';
import { RiskRegisterPage } from 'components/pages/RiskRegisterPage';
import { ScheduleSimulation } from 'components/pages/ScheduleSimulationPage';

export const sideMenu: SideMenuListProps[] = [
    {
        ...excellenceAppsConfig.kcRecommendations,
        id: 'Recommendations',
        muiIcon: <MuiIcon icon={'assistant'} fontSize="2.5rem" color="#9eadc1" />,
        permissions: 'Recommendations',
    },
    {
        ...excellenceAppsConfig.riskRegister,
        id: 'riskRegister',
        muiIcon: <MuiIcon icon={'app_registration_icon'} fontSize="2.5rem" color="#9eadc1" />,
        permissions: 'RiskRegister',
    },
    {
        ...excellenceAppsConfig.durationForecast,
        id: 'DurationForecast',
        icon: 'duration-forecast',
        permissions: 'ClusterAnalysis',
    },
    {
        ...excellenceAppsConfig.logicQualityCheck,
        id: 'LogicQualityCheck',
        icon: 'Single-quality-check',
        permissions: 'LogicChecker',
    },
    {
        ...excellenceAppsConfig.completionPlan,
        id: 'completionPlan',
        muiIcon: <MuiIcon icon={'flaky'} fontSize="2.5rem" color="#9eadc1" />,
        permissions: 'CompletionPlan',
    },
    {
        ...excellenceAppsConfig.finishDatePrediction,
        id: 'finishDatePrediction',
        muiIcon: <MuiIcon icon={'visibility_icon'} fontSize="2.5rem" color="#9eadc1" />,
        permissions: 'FinishDatePrediction',
    },
    {
        ...excellenceAppsConfig.scheduleSimulation,
        id: 'ScheduleSimulation',
        muiIcon: <MuiIcon icon={'update-icon'} fontSize="2.5rem" color="#9eadc1" />,
        permissions: 'ScheduleSimulation',
    },
    {
        ...excellenceAppsConfig.learningMaterials,
        id: 'LearningMaterials',
        icon: 'Academia-Icon',
        permissions: 'LMS',
    },
    {
        ...excellenceAppsConfig.activitiesGraph,
        id: 'ActivitiesGraph',
        icon: 'Menu---Cluster-RCF-Analysis',
        isAdmin: true,
        permissions: 'Admin',
    },
];

export const ExcellenceAppsRoutes = () => {
    const { path } = useRouteMatch();
    const { ability } = useUserHook();
    const RecommendationsAbility = ability.can('view', 'Recommendations');
    const clusterAnalysisAbility = ability.can('view', 'ClusterAnalysis');
    const logicCheckerAbility = ability.can('view', 'LogicChecker');
    const CompletionPlanAbility = ability.can('view', 'CompletionPlan');
    const ScheduleSimulationAbility = ability.can('view', 'ScheduleSimulation');
    const FinishDatePredictionAbility = ability.can('view', 'FinishDatePrediction');
    const redirect = sideMenu.find((item) => {
        return item.permissions && ability.can('view', item.permissions);
    });

    //TODO: create component for wrapping just routes that contains the view (programWrapper, SideDashboardMenu, mainColumn, TopHeaderContainer)
    return (
        <Responsive sideMenu={sideMenu} title={'Excellence Apps'}>
            <Switch>
                <GuardedRoute
                    path={`${path}/${excellenceAppsConfig.kcRecommendations.link}/:id/:versionToCompareId?`}
                    component={RecommendationsPage}
                    auth={RecommendationsAbility}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${excellenceAppsConfig.kcRecommendations.link}`}
                    component={() => (
                        <RedirectToLast
                            to={`${path}/${excellenceAppsConfig.kcRecommendations.link}`}
                            title={excellenceAppsConfig.kcRecommendations.title}
                        />
                    )}
                    auth={RecommendationsAbility}
                    redirect={`${path}/no-access`}
                />

                <GuardedRoute
                    path={`${path}/${excellenceAppsConfig.durationForecast.link}/:id`}
                    component={DurationForecastPage}
                    auth={clusterAnalysisAbility}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${excellenceAppsConfig.durationForecast.link}`}
                    component={() => (
                        <RedirectToLast
                            to={`${path}/${excellenceAppsConfig.durationForecast.link}`}
                            title={excellenceAppsConfig.durationForecast.title}
                        />
                    )}
                    auth={clusterAnalysisAbility}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${excellenceAppsConfig.logicQualityCheck.link}/:id/:category/:check/activities`}
                    component={LogicQualityCheckActivitiesPage}
                    auth={logicCheckerAbility}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${excellenceAppsConfig.logicQualityCheck.link}/:id/:category/:check`}
                    component={LogicQualityCheckValidationPage}
                    auth={logicCheckerAbility}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${excellenceAppsConfig.logicQualityCheck.link}/:id/:category`}
                    component={LogicQualityCheckCategoryPage}
                    auth={logicCheckerAbility}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${excellenceAppsConfig.logicQualityCheck.link}/:id`}
                    component={LogicQualityCheckSummaryPage}
                    auth={logicCheckerAbility}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${excellenceAppsConfig.logicQualityCheck.link}`}
                    component={() => (
                        <RedirectToLast
                            to={`${path}/${excellenceAppsConfig.logicQualityCheck.link}`}
                            title={excellenceAppsConfig.logicQualityCheck.title}
                        />
                    )}
                    auth={logicCheckerAbility}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${excellenceAppsConfig.completionPlan.link}/:id([0-9-]+)`}
                    component={CompletionPlanPage}
                    auth={CompletionPlanAbility}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${excellenceAppsConfig.completionPlan.link}`}
                    component={() => (
                        <RedirectToLast
                            to={`${path}/${excellenceAppsConfig.completionPlan.link}`}
                            title={excellenceAppsConfig.completionPlan.title}
                        />
                    )}
                    auth={CompletionPlanAbility}
                    redirect={`${path}/no-access`}
                />

                <GuardedRoute
                    path={`${path}/${excellenceAppsConfig.finishDatePrediction.link}/:id`}
                    component={FinishDatePredictionPage}
                    auth={FinishDatePredictionAbility}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${excellenceAppsConfig.finishDatePrediction.link}`}
                    component={() => (
                        <RedirectToLast
                            to={`${path}/${excellenceAppsConfig.finishDatePrediction.link}`}
                            title={excellenceAppsConfig.finishDatePrediction.title}
                        />
                    )}
                    auth={FinishDatePredictionAbility}
                    redirect={`${path}/no-access`}
                />

                <GuardedRoute
                    path={`${path}/${excellenceAppsConfig.scheduleSimulation.link}/:id`}
                    component={ScheduleSimulation}
                    auth={ScheduleSimulationAbility}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${excellenceAppsConfig.scheduleSimulation.link}`}
                    component={() => (
                        <RedirectToLast
                            to={`${path}/${excellenceAppsConfig.scheduleSimulation.link}`}
                            title={excellenceAppsConfig.scheduleSimulation.title}
                        />
                    )}
                    auth={ScheduleSimulationAbility}
                    redirect={`${path}/no-access`}
                />

                <GuardedRoute
                    path={`${path}/${excellenceAppsConfig.learningMaterials.link}`}
                    component={LmsPage}
                    auth={ability.can('view', 'LMS')}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${excellenceAppsConfig.activitiesGraph.link}/:id`}
                    component={ActivitiesGraphPage}
                    auth={ability.can('all', 'Admin')}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${excellenceAppsConfig.activitiesGraph.link}`}
                    component={() => (
                        <RedirectToLast
                            to={`${path}/${excellenceAppsConfig.activitiesGraph.link}`}
                            title={excellenceAppsConfig.activitiesGraph.title}
                        />
                    )}
                    auth={ability.can('all', 'Admin')}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${excellenceAppsConfig.riskRegister.link}/:id`}
                    component={RiskRegisterPage}
                    auth={ability.can('view', 'RiskRegister')}
                    redirect={`${path}/no-access`}
                />

                <GuardedRoute
                    path={`${path}/${excellenceAppsConfig.riskRegister.link}`}
                    component={() => (
                        <RedirectToLast
                            to={`${path}/${excellenceAppsConfig.riskRegister.link}`}
                            title={excellenceAppsConfig.riskRegister.title}
                        />
                    )}
                    auth={ability.can('view', 'RiskRegister')}
                    redirect={`${path}/no-access`}
                />

                <Route path={`${path}/no-access`} component={NoAccess} />
                <Route exact path={path}>
                    <Redirect to={`${path}/${redirect?.link}`} />
                </Route>
                <Route path="*" component={PageNotFound} />
            </Switch>
        </Responsive>
    );
};
